/**
 * Input/output contracts for the auth-domain callable functions
 * (`activateAccount`, `setUserRole`, `syncAdminMemberships`).
 *
 * These were previously declared inline in each functions/src file. Pulling
 * them into the shared package gives the callable wrapper a single Zod
 * input schema to parse against, and gives the admin client a single
 * import path for request payload typing.
 */

import { z } from 'zod';
import { roleSchema } from '../schemas/role';

// ── activateAccount ──

/**
 * Account activation. When `code` is provided the callable claims the
 * single matching invitation; when omitted the callable claims every
 * pending invitation whose email matches the caller's authenticated
 * email (the "first sign-in by email-match" path).
 */
export const activateAccountInputSchema = z
  .object({
    code: z.string().min(1).optional(),
  })
  .strict();

export type ActivateAccountInput = z.infer<typeof activateAccountInputSchema>;

export interface ActivateAccountResponse {
  success: true;
  /** UID of the activated user. */
  userId: string;
  /** Number of pending invitations claimed in this activation. */
  invitationsClaimed: number;
}

// ── setUserRole ──

/**
 * Sets a user's global role + mirrors it onto every existing
 * eventMembership. Admin-only.
 */
export const setUserRoleInputSchema = z
  .object({
    targetUserId: z.string().min(1),
    role: roleSchema,
  })
  .strict();

export type SetUserRoleInput = z.infer<typeof setUserRoleInputSchema>;

export interface SetUserRoleResponse {
  success: true;
}

// ── syncAdminMemberships ──

/**
 * No-input admin maintenance callable: scans every eventMembership and
 * flips role to match the user's current global role. Used after a
 * bulk role import or when the role-mirror trigger has drifted.
 */
export const syncAdminMembershipsInputSchema = z.object({}).strict();

export type SyncAdminMembershipsInput = z.infer<
  typeof syncAdminMembershipsInputSchema
>;

export interface SyncAdminMembershipsResponse {
  success: true;
  membershipsUpdated: number;
}

// ── exchangeMicrosoftToken ──

/**
 * Exchanges the short-lived ID token returned by the native iOS MSAL SDK for
 * a Firebase custom token. The Microsoft token is bearer material: callers
 * must keep it in memory and must never persist or log it.
 */
export const exchangeMicrosoftTokenInputSchema = z
  .object({
    idToken: z.string().min(100).max(16_384),
  })
  .strict();

export type ExchangeMicrosoftTokenInput = z.infer<
  typeof exchangeMicrosoftTokenInputSchema
>;

export interface ExchangeMicrosoftTokenResponse {
  firebaseCustomToken: string;
  mappingOutcome: 'existing-link' | 'provider-migration' | 'native-msal';
}
